/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { GetOrders } from '../../components/GetOrders';
import Loading from '../../components/Loading';
import { SelectOrder } from '../../components/SelectOrder';
import { ValidationService } from '../../components/ValidationService';
import { useFetchOrder, type BuscarPedidoPayload } from '../../hooks/useFetchOrder';
import { digitsOnly } from '../../utils/documento';
import { Order } from '../../types/types';
import { DeepLinkResolveModal } from './components';
import type { DeepLinkSubmitPayload } from './components';

const BUSCAR_PEDIDO_STORAGE_KEY = 'buscarPedidoPayload';

function persistBuscarPedidoPayload(payload: BuscarPedidoPayload) {
  sessionStorage.removeItem('pedidoEcommerce');
  sessionStorage.removeItem('searchedOrderId');
  sessionStorage.removeItem('searchedDocument');
  sessionStorage.setItem(BUSCAR_PEDIDO_STORAGE_KEY, JSON.stringify(payload));
}

type DeepLinkRefs = {
  orderId?: string;
  idGar?: string;
};

const GetOrdersStep = () => {
  const navigate = useNavigate();
  const [deepLinkRefs, setDeepLinkRefs] = useState<DeepLinkRefs | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('order_id')?.trim() || undefined;
    const idGar = urlParams.get('idGar')?.trim() || undefined;
    const documentParam = urlParams.get('document');
    const document = documentParam ? digitsOnly(documentParam) : '';

    if ((orderId || idGar) && document) {
      persistBuscarPedidoPayload({
        ...(orderId ? { order_id: orderId, pedidoEcommerce: orderId } : {}),
        ...(idGar ? { idGar: digitsOnly(idGar) } : {}),
        document,
      });
      navigate('/selecionar', { replace: true });
      return;
    }

    if (orderId || idGar) {
      setDeepLinkRefs({ orderId, idGar });
    }
  }, [navigate]);

  const handleFetchAndNavigate = (orderId: string, document: string) => {
    persistBuscarPedidoPayload({
      idGar: digitsOnly(orderId),
      document: digitsOnly(document),
    });
    navigate('/selecionar');
  };

  const handleDeepLinkSubmit = (payload: DeepLinkSubmitPayload) => {
    persistBuscarPedidoPayload({
      ...(payload.idGar ? { idGar: payload.idGar } : {}),
      ...(payload.order_id
        ? {
            order_id: payload.order_id,
            pedidoEcommerce: payload.pedidoEcommerce ?? payload.order_id,
          }
        : {}),
      ...(payload.pedidoEcommerce && !payload.order_id
        ? { pedidoEcommerce: payload.pedidoEcommerce, order_id: payload.pedidoEcommerce }
        : {}),
      document: payload.document,
    });
    navigate('/selecionar');
  };

  const handleDeepLinkCancel = () => {
    setDeepLinkRefs(null);
    navigate('/', { replace: true });
  };

  if (deepLinkRefs) {
    return (
      <DeepLinkResolveModal
        orderId={deepLinkRefs.orderId}
        idGar={deepLinkRefs.idGar}
        loading={false}
        onSubmit={handleDeepLinkSubmit}
        onCancel={handleDeepLinkCancel}
      />
    );
  }

  return <GetOrders fetchOrder={handleFetchAndNavigate} />;
};

const SelectOrderStep = () => {
  const navigate = useNavigate();
  const {
    order,
    loading,
    error,
    statuses,
    fetchOrderByBuscarPedido,
    fetchOrderByGarDocument,
    setStatuses,
  } = useFetchOrder();
  const [errorGeneric, setError] = useState<string>();

  useEffect(() => {
    const storedPayload = sessionStorage.getItem(BUSCAR_PEDIDO_STORAGE_KEY);
    if (storedPayload) {
      try {
        const payload = JSON.parse(storedPayload) as BuscarPedidoPayload;
        if (payload.document) {
          fetchOrderByBuscarPedido(payload);
          return;
        }
      } catch {
        sessionStorage.removeItem(BUSCAR_PEDIDO_STORAGE_KEY);
      }
    }

    const garId = sessionStorage.getItem('searchedOrderId');
    const document = sessionStorage.getItem('searchedDocument');

    if (garId && document) {
      fetchOrderByGarDocument(garId, document);
      return;
    }

    navigate('/', { replace: true });
  }, []);

  useEffect(() => {
    if (order && order[0]?.codigo === 265) {
      setError('Erro ao buscar pedidos. Tente novamente.');
    }
  }, [order]);

  const handleSchedule = (selectedOrder: Order | null) => {
    if (selectedOrder) {
      sessionStorage.setItem('selectedOrderForValidation', JSON.stringify(selectedOrder));
      navigate('/validar');
    } else {
      console.error('Nenhum pedido selecionado.');
    }
  };

  const handleCancelSchedule = (orderId: string) => {
    const updatedStatuses = { ...statuses, [orderId]: false };
    setStatuses(updatedStatuses);
  };

  if (loading) return <Loading loading={loading} />;
  if (error || errorGeneric) {
    return (
      <div className="flex flex-col gap-10">
        <p className="text-feedback-negative text-xl">{error || errorGeneric}</p>
        <a
          href="/"
          className="flex items-center justify-center max-w-60 h-10 bg-ins-blue text-white font-bold rounded-lg"
        >
          Voltar
        </a>
      </div>
    );
  }
  return (
    <SelectOrder
      orders={order ? [order] : []}
      statuses={statuses}
      onSchedule={handleSchedule}
      onCancelSchedule={handleCancelSchedule}
    />
  );
};

const ValidationStep = () => {
  const location = useLocation();

  let orderForValidation: Order | null = location.state?.selectedOrder;

  if (!orderForValidation) {
    const storedOrderJSON = sessionStorage.getItem('selectedOrderForValidation');
    if (storedOrderJSON) {
      orderForValidation = JSON.parse(storedOrderJSON);
    }
  }

  if (!orderForValidation) {
    return <Navigate to="/" replace />;
  }

  return <ValidationService order={orderForValidation} />;
};

export const Scheduling = () => {
  return (
    <div className="max-w-full md:max-w-[1295px] w-full md:mt-12 mt-0 mx-auto md:px-4 md:bg-transparent">
      <Routes>
        <Route index element={<GetOrdersStep />} />
        <Route path="selecionar" element={<SelectOrderStep />} />
        <Route path="validar" element={<ValidationStep />} />
      </Routes>
    </div>
  );
};
